import { useState, useEffect } from 'react';
import { ArrowLeft, ChevronRight, Tag } from 'lucide-react';
import ContactPopup from './ContactPopup';

// Sample solutions data
export const SOLUTIONS = [
  {
    id: 1,
    title: 'Mailer Bot',
    category: 'Communication',
    description: 'Intelligent email automation for streamlined communication.',
    image: 'https://images.unsplash.com/photo-1596526131083-e8c633c948d2?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1200&h=600&q=80',
    fullDescription: 'Our Mailer Bot solution leverages advanced AI to automate email communication workflows, saving time and ensuring consistent messaging across your organization.',
    features: [
      'Intelligent email drafting  and reading based on context and intent',
      'Automated follow-ups and response tracking',
      'Smart scheduling for optimal send times',
      'Personalization at scale with AI-driven content customization',
      'Integration with all major email platforms'
    ],
    useCases: [
      'Marketing campaigns with personalized follow-ups',
      'HR management-leave requests,payslips,etc...',
      'Sales outreach with dynamic content adaptation',
      'Customer support email automation',
      'Internal communication streamlining'
    ],
    benefits: [
      'Reduce email management time by up to 70%',
      'Improve response rates with optimized sending times',
      'Ensure consistent brand voice across all communications',
      'Scale your email operations without adding headcount'
    ]
  },
  {
    id: 2,
    title: 'Teams Chatbot',
    category: 'Workplace',
    description: 'AI assistant for scheduling meetings and HR management within Microsoft Teams.',
    image: 'https://images.unsplash.com/photo-1611174895360-53948c735c3a?q=80&w=2070&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
    fullDescription: 'Our Teams Chatbot solution seamlessly integrates with Microsoft Teams to automate scheduling, HR processes, and team coordination tasks through natural language conversations.',
    features: [
      'Natural language meeting scheduling and rescheduling',
      'HR policy lookups and common HR request automation',
      'Team availability management and optimal meeting time suggestions',
      'Meeting notes transcription and action item tracking',
      'Integration with calendar systems and HR databases'
    ],
    useCases: [
      'Simplified meeting scheduling across timezones',
      'Automated HR onboarding processes',
      'Employee leave management and approvals',
      'Team coordination and resource allocation'
    ],
    benefits: [
      'Reduce scheduling back-and-forth by 90%',
      'Free up HR staff time for strategic initiatives',
      'Improve team coordination and meeting effectiveness',
      'Provide 24/7 access to HR information and services'
    ]
  },
  {
    id: 3,
    title: 'Hospitality Agent',
    category: 'Hospitality',
    description: 'AI-powered virtual concierge for hotels and hospitality businesses.',
    image: 'https://images.unsplash.com/photo-1591121432666-6cb433b6dd2d?q=80&w=2074&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
    fullDescription: 'Our Hospitality Agent transforms guest experiences with an AI-powered virtual concierge that handles bookings, answers questions, and provides personalized recommendations 24/7.',
    features: [
      'Natural language booking and reservation management',
      'Instant response to guest inquiries in multiple languages',
      'Personalized local recommendations based on guest preferences',
      'Seamless integration with property management systems',
      'Automated check-in/check-out assistance'
    ],
    useCases: [
      'Hotel virtual concierge services',
      'Restaurant reservation and menu assistance',
      'Vacation rental guest support',
      'Event venue information and booking'
    ],
    benefits: [
      'Provide 24/7 guest support without staffing costs',
      'Increase direct bookings and reduce dependency on OTAs',
      'Improve guest satisfaction with instant response times',
      'Collect valuable guest preference data for personalization'
    ]
  },
  {
    id: 4,
    title: 'CRM Chatbot',
    category: 'Sales',
    description: 'AI-powered assistant for customer relationship management.',
    image: 'https://images.unsplash.com/photo-1556745757-8d76bdb6984b?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1200&h=600&q=80',
    fullDescription: 'Our CRM Chatbot solution enhances customer relationship management with AI-powered assistance for sales teams, automating data entry, providing insights, and suggesting next best actions.',
    features: [
      'Automated CRM data entry and enrichment',
      'AI-driven sales opportunity identification',
      'Conversation summaries and follow-up recommendations',
      'Cross-sell and upsell suggestions based on customer data',
      'Integration with major CRM platforms like Salesforce, HubSpot, and more'
    ],
    useCases: [
      'Sales process automation and acceleration',
      'Post-meeting CRM updates and action planning',
      'Customer communication analysis and improvement',
      'Sales coaching and best practice implementation'
    ],
    benefits: [
      'Reduce CRM data entry time by 80%',
      'Increase sales team productivity and deal velocity',
      'Improve data quality and completeness in your CRM',
      'Enhance customer insights for better sales outcomes'
    ]
  },
  {
    id: 5,
    title: 'Vector Database Applications',
    category: 'Legal',
    description: 'AI-powered legal research assistant for case libraries and similar case finding.',
    image: 'https://images.unsplash.com/photo-1664854953181-b12e6dda8b7c?q=80&w=1964&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
    fullDescription: 'Our Vector Database Application for legal research transforms how legal professionals find relevant cases and precedents, using advanced vector search to identify semantically similar cases beyond keyword matching.',
    features: [
      'Semantic search across case libraries using vector embeddings',
      'Automated case brief generation and summarization',
      'Precedent identification based on factual similarities',
      'Legal argument suggestion based on similar successful cases',
      'Integration with major legal research databases'
    ],
    useCases: [
      'Legal research acceleration and depth',
      'Case strategy development with historical insights',
      'Moot court and law school education',
      'Cross-jurisdictional precedent finding'
    ],
    benefits: [
      'Reduce legal research time by up to 70%',
      'Discover relevant cases missed by traditional keyword search',
      'Build stronger legal arguments with comprehensive precedent analysis',
      'Increase efficiency and capacity of legal teams'
    ]
  }
];

// Header Component (same as in ProjectDetailPage)
const Header = ({ openContactPopup }) => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header className={`fixed w-full z-50 transition-all duration-300 ${scrolled ? 'bg-black bg-opacity-90 py-2' : 'bg-transparent py-4'}`}>
      <div className="container mx-auto px-4 flex justify-between items-center">
        <div className="flex items-center">
          {/* Logo */}
          <div className="flex items-center">
            <svg 
              width="40" 
              height="32" 
              viewBox="0 0 100 80" 
              className="fill-current text-red-600"
            >
              <path d="M50,0 L95,60 L80,60 L50,20 L20,60 L5,60 L50,0 Z M40,70 L60,70 L60,80 L40,80 L40,70 Z" />
            </svg>
            <span className="ml-2 text-xl font-bold text-white">PHOEN<span className="text-red-600">AI</span>X</span>
          </div>
        </div>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center space-x-8">
          <a href="/#about" className="text-white hover:text-red-500 transition-colors">Home</a>
          <a href="/projects/1" className="text-white hover:text-red-500 transition-colors">Projects</a>
          <a href="/solutions" className="text-white hover:text-red-500 transition-colors">Solutions</a>
          <button 
            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded"
            onClick={openContactPopup}
          >
            Contact Us
          </button>
        </nav>
      </div>
    </header>
  );
};

// Solution Card Component for the grid
const SolutionCard = ({ solution, onClick }) => {
  return (
    <div 
      className="group bg-gray-900 rounded-lg overflow-hidden transition-all duration-300 hover:shadow-xl hover:shadow-red-900/20 cursor-pointer" 
      style={{ height: '400px' }}
      onClick={() => onClick(solution.id)}
    >
      <div className="h-1/2 overflow-hidden">
        <img 
          src={solution.image} 
          alt={solution.title} 
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
      </div>
      <div className="p-6 h-1/2 flex flex-col justify-between">
        <div>
          <span className="text-red-500 text-sm font-medium mb-2 block">{solution.category}</span>
          <h3 className="text-xl font-bold text-white mb-2">{solution.title}</h3>
          <p className="text-gray-400">{solution.description}</p>
        </div>
      </div>
    </div>
  );
};

// Solutions Grid Component
const SolutionsGrid = ({ onSolutionClick }) => {
  return (
    <section className="py-32 bg-black">
      <div className="container mx-auto px-4">
        <h1 className="text-4xl md:text-5xl font-bold text-white mb-12 text-center">Our <span className="text-red-600">Solutions</span></h1>
        <p className="text-xl text-gray-300 mb-16 text-center max-w-3xl mx-auto">
          PHOENAIX offers a range of AI-powered solutions designed to solve specific business challenges across industries.
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {SOLUTIONS.map((solution) => (
            <SolutionCard 
              key={solution.id} 
              solution={solution} 
              onClick={onSolutionClick}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

// Solution Detail Component
const SolutionDetail = ({ solution, goBack, openContactPopup }) => {
  return (
    <div className="min-h-screen bg-black">
      {/* Hero Section */}
      <section className="relative pt-32 pb-16 bg-black">
        <div 
          className="absolute inset-0 bg-gradient-to-br from-black via-black to-red-900 opacity-40"
          style={{ 
            backgroundImage: 'radial-gradient(circle at top right, rgba(224, 31, 38, 0.2), transparent 70%)' 
          }}
        />
        
        <div className="container mx-auto px-4 relative z-10">
          <button 
            onClick={goBack}
            className="inline-flex items-center text-gray-400 hover:text-red-500 mb-6 transition-colors"
          >
            <ArrowLeft size={16} className="mr-2" /> Back to Solutions
          </button>
          
          <div className="max-w-4xl">
            <span className="text-red-500 text-sm font-medium mb-2 block">{solution.category}</span>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">{solution.title}</h1>
            <p className="text-xl text-gray-300 mb-8">{solution.description}</p>
          </div>
        </div>
      </section>

      {/* Featured Image */}
      <section className="bg-black py-8">
        <div className="container mx-auto px-4">
          <div className="relative rounded-lg overflow-hidden">
            <img 
              src={solution.image} 
              alt={solution.title} 
              className="w-full object-cover h-96 md:h-auto"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent opacity-60"></div>
          </div>
        </div>
      </section>

      {/* Solution Content */}
      <section className="bg-black py-12">
        <div className="container mx-auto px-4">
          <div className="flex flex-col lg:flex-row gap-12">
            {/* Main Content */}
            <div className="lg:w-2/3">
              <div className="prose prose-invert prose-red max-w-none">
                <p className="text-xl text-gray-300 mb-8">{solution.fullDescription}</p>
                
                <h2 className="text-2xl font-bold text-white mt-12 mb-6">Key Features</h2>
                <ul className="list-disc pl-6 mb-8 space-y-2 text-gray-300">
                  {solution.features.map((feature, index) => (
                    <li key={index}>{feature}</li>
                  ))}
                </ul>

                <h2 className="text-2xl font-bold text-white mt-12 mb-6">Use Cases</h2>
                <ul className="list-disc pl-6 mb-8 space-y-2 text-gray-300">
                  {solution.useCases.map((useCase, index) => (
                    <li key={index}>{useCase}</li>
                  ))}
                </ul>

                <h2 className="text-2xl font-bold text-white mt-12 mb-6">Benefits</h2>
                <ul className="list-disc pl-6 mb-8 space-y-2 text-gray-300">
                  {solution.benefits.map((benefit, index) => (
                    <li key={index}>{benefit}</li>
                  ))}
                </ul>
              </div>
              
              {/* Call to Action */}
              <div className="mt-16 bg-gradient-to-r from-gray-900 to-black p-8 rounded-lg border border-gray-800">
                <h3 className="text-2xl font-bold text-white mb-4">Ready to implement this solution?</h3>
                <p className="text-gray-300 mb-6">Schedule a demo to see how our {solution.title} can transform your business operations.</p>
                <div className="flex flex-wrap gap-4">
                  <button 
                    className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded flex items-center"
                    onClick={openContactPopup}
                  >
                    Request a Demo <ChevronRight className="ml-2" size={20} />
                  </button>
                </div>
              </div>
            </div>
            
            {/* Sidebar */}
            <div className="lg:w-1/3">
              <div className="sticky top-32">
                <div className="bg-gray-900 rounded-lg p-6 mb-8">
                  <h3 className="text-xl font-bold text-white mb-4">Industries</h3>
                  <div className="flex flex-wrap gap-2">
                    {['Enterprise', 'Healthcare', 'Finance', 'Retail', 'Legal'].map((industry) => (
                      <span key={industry} className="bg-gray-800 text-gray-300 px-3 py-1 rounded-full text-sm flex items-center">
                        <Tag size={12} className="mr-1" />
                        {industry}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

// Footer Component (same as in ProjectDetailPage)
const Footer = ({ openContactPopup }) => {
  return (
    <footer className="bg-black text-white py-12">
      <div className="container mx-auto px-4">
        <div className="flex flex-col md:flex-row justify-between mb-12">
          <div className="mb-8 md:mb-0">
            <div className="flex items-center mb-4">
              <svg 
                width="32" 
                height="24" 
                viewBox="0 0 100 80" 
                className="fill-current text-red-600"
              >
                <path d="M50,0 L95,60 L80,60 L50,20 L20,60 L5,60 L50,0 Z M40,70 L60,70 L60,80 L40,80 L40,70 Z" />
              </svg>
              <span className="ml-2 text-xl font-bold">PHOEN<span className="text-red-600">AI</span>X</span>
            </div>
            <p className="text-gray-400 max-w-md">
              Transforming industries through innovative AI solutions. 
              Our intelligent agents and applications power the future of business.
            </p>
          </div>
          
          <div className="w-full flex flex-col items-end">
  <div className="bg-gray-800 p-4 rounded-lg shadow-lg mt-auto">
    <h3 className="text-lg font-semibold mb-4 text-white">Contact Us</h3>
    <ul className="space-y-2">
      <li><a href="tel:+917200957597" className="text-gray-400 hover:text-red-500 transition-colors">+91 72009 57597</a></li>
      <li><a href="mailto:phoenaixworkspace@gmail.com" className="text-gray-400 hover:text-red-500 transition-colors">phoenaixworkspace@gmail.com</a></li>
      <li>
        <button 
          onClick={openContactPopup} 
          className="text-gray-400 hover:text-red-500 transition-colors"
        >
          Send us a message
        </button>
      </li>
    </ul>
  </div>
</div>
        </div>
        
        <div className="pt-8 border-t border-gray-800 text-center md:text-left flex flex-col md:flex-row justify-between items-center">
          <p className="text-gray-500 text-sm">© 2025 PHOENAIX. All rights reserved.</p>
          <div className="flex space-x-6 mt-4 md:mt-0">
            <a href="/privacy" className="text-gray-400 hover:text-red-500 transition-colors">Privacy Policy</a>
            <a href="/terms" className="text-gray-400 hover:text-red-500 transition-colors">Terms of Service</a>
            <a href="/cookies" className="text-gray-400 hover:text-red-500 transition-colors">Cookies</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Main SolutionsPage Component
export default function SolutionsPage() {
  const [currentSolutionId, setCurrentSolutionId] = useState(null);
  const [isContactPopupOpen, setIsContactPopupOpen] = useState(false);
  
  const openContactPopup = () => {
    setIsContactPopupOpen(true);
  };
  
  const closeContactPopup = () => {
    setIsContactPopupOpen(false);
  };
  
  useEffect(() => {
    const handleNavigation = () => {
      const path = window.location.pathname;
      if (path === '/solutions') {
        setCurrentSolutionId(null);
      } else if (path.startsWith('/solutions/')) {
        const id = parseInt(path.split('/').pop());
        if (!isNaN(id)) {
          setCurrentSolutionId(id);
        }
      }
    };

    // Initial check
    handleNavigation();

    // Listen for popstate events (back/forward navigation)
    window.addEventListener('popstate', handleNavigation);
    
    return () => {
      window.removeEventListener('popstate', handleNavigation);
    };
  }, []);

  // Handle solution click
  const handleSolutionClick = (solutionId) => {
    window.history.pushState({}, '', `/solutions/${solutionId}`);
    setCurrentSolutionId(solutionId);
    window.scrollTo(0, 0);
  };

  // Go back to solutions grid
  const goBack = () => {
    window.history.pushState({}, '', '/solutions');
    setCurrentSolutionId(null);
    window.scrollTo(0, 0);
  };

  // Find the current solution
  const currentSolution = currentSolutionId 
    ? SOLUTIONS.find(s => s.id === currentSolutionId)
    : null;

  useEffect(() => {
    document.body.style.backgroundColor = '#000000';
  }, []);

  return (
    <div className="min-h-screen bg-black text-white">
      <Header openContactPopup={openContactPopup} />
      
      {currentSolution ? (
        <SolutionDetail solution={currentSolution} goBack={goBack} openContactPopup={openContactPopup} />
      ) : (
        <SolutionsGrid onSolutionClick={handleSolutionClick} />
      )}
      
      <Footer openContactPopup={openContactPopup} />
      <ContactPopup isOpen={isContactPopupOpen} onClose={closeContactPopup} />
    </div>
  );
}